"use client";

import {
  Text,
  Button,
  FaLeaf,
  TbEye,
  TbRuler,
  TbCalendar,
} from "@forestsync/ui";
import Link from "next/link";
import { type CarbonSink } from "@/types/glassroom";
import { useTranslations } from "next-intl";

interface CarbonSinkRowProps {
  sink: CarbonSink;
  className?: string;
}

export function CarbonSinkRow({ sink, className }: CarbonSinkRowProps) {
  const t = useTranslations();

  return (
    <div
      className={`py-2 sm:py-3 px-3 sm:px-4 fs-hover-surface transition-colors duration-150 rounded-md ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Name and ID - First Line */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1 sm:mb-2">
            <Text
              fw={600}
              size="sm"
              className="text-gray-900 dark:text-white truncate"
            >
              {sink.name}
            </Text>
            <Text
              size="xs"
              className="text-gray-400 dark:text-gray-500 font-mono opacity-75 flex-shrink-0"
            >
              {sink.refId}
            </Text>
          </div>

          {/* Stats - More compact on mobile */}
          <div className="flex flex-wrap sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <TbRuler
                size={10}
                className="text-green-600 dark:text-green-400"
              />
              {(sink.sizeHa ?? 0).toLocaleString()}
              {t("common.hectares")}
            </span>
            <span className="flex items-center gap-1">
              <FaLeaf size={10} className="text-blue-600 dark:text-blue-400" />
              {(sink.co2Storage ?? 0).toLocaleString()}
              {t("common.tons")} {t("common.co2")}
            </span>
            <span className="flex items-center gap-1">
              <TbCalendar
                size={10}
                className="text-amber-600 dark:text-amber-400"
              />
              {t("common.yearsOld", { age: sink.age })}
            </span>
          </div>
        </div>

        {/* Action button - smaller on mobile */}
        <Link href={`/sink/${sink.refId}`}>
          <Button
            variant="light"
            size="xs"
            leftSection={<TbEye size={14} />}
            className="w-full sm:w-auto flex-shrink-0 mt-1 sm:mt-0"
          >
            {t("common.exploreProject")}
          </Button>
        </Link>
      </div>
    </div>
  );
}
